<template>
    <div
        :id="id"
        :class="{ filled, focused, disabled, error: isError }"
        class="tl-textarea"
    >
        <div class="tl-textarea-wrapper">
            <textarea
                ref="input"
                v-model="modelState"
                type="text"
                :disabled="disabled"
                max-rows="10"
                @focus="focused = true"
                @blur="focused = false"
                @keyup="handleKey"
            />
            <div class="tl-label">
                <div v-html="label" />
            </div>
        </div>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import useTools from 'widget-components/utilities/tools'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import marked from '../../utilities/marked'
import { HTMLTextAreaElement } from 'happy-dom'
import type { BlockPath } from 'widget-components/integrations/CustomPage/types'

const { wait } = useTools()

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id?: string
    modelValue: string
    disabled?: boolean
    focus?: boolean
    error?: string | boolean
    label?: string
    initialValue?: string
    blockPath?: BlockPath
}>()

const emit = defineEmits(['update:modelValue', 'submit'])

const input = ref<HTMLTextAreaElement>()
const modelState = ref(props.modelValue)
const focused = ref(false)

const label = computed(() => marked(props.label))
const filled = computed(() => !!modelState.value)
const isError = computed(() => !!props.error)

onMounted(async () => {
    if (props.focus) {
        await wait(800)
        input.value?.focus()
    }

    if (props.initialValue) {
        modelState.value = props.initialValue
    }
})

watch(modelState, (val) => {
    emit('update:modelValue', val)
})

watch(() => props.modelValue, (val) => {
    if (val !== modelState.value) modelState.value = val
})

const handleKey = (event: KeyboardEvent) => {
    const code = event.keyCode

    // ENTER
    if (code === 13 || event.key === 'enter') {
        emit('submit')
    }
}
</script>

<style lang="sass" scoped>
@import './InputTextarea.sass'
</style>
